import { AxiosError } from "axios";

/**
 * Authentication error codes that can occur in the customer portal
 */
export const AuthErrorCode = {
    // Login errors
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
    ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',

    // Token errors
    INVALID_TOKEN: 'INVALID_TOKEN',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_ALREADY_USED: 'TOKEN_ALREADY_USED',
    TOKEN_REFRESH_FAILED: 'TOKEN_REFRESH_FAILED',

    // Network/Server errors
    NETWORK_ERROR: 'NETWORK_ERROR',
    SERVER_ERROR: 'SERVER_ERROR',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

    // Validation errors
    INVALID_EMAIL: 'INVALID_EMAIL',
    INVALID_PASSWORD: 'INVALID_PASSWORD',

    // Feature not available
    NOT_SUPPORTED: 'NOT_SUPPORTED',
    NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',

    // Generic
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type AuthErrorCode = typeof AuthErrorCode[keyof typeof AuthErrorCode];

/**
 * Custom error class for authentication errors
 */
export class AuthError extends Error {
    public readonly code: AuthErrorCode;
    public readonly statusCode?: number;
    public readonly originalError?: unknown;

    constructor(
        code: AuthErrorCode,
        message: string,
        statusCode?: number,
        originalError?: unknown
    ) {
        super(message);
        this.name = 'AuthError';
        this.code = code;
        this.statusCode = statusCode;
        this.originalError = originalError;

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if ('captureStackTrace' in Error && typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, AuthError);
        }
    }

    /**
     * Check if an error is an AuthError
     */
    static isAuthError(error: unknown): error is AuthError {
        return error instanceof AuthError;
    }
}

/**
 * German translations for error messages
 */
const errorMessages: Record<AuthErrorCode, string> = {
    [AuthErrorCode.INVALID_CREDENTIALS]: 'Die Anmeldedaten sind ungültig. Bitte überprüfen Sie E-Mail und Passwort.',
    [AuthErrorCode.ACCOUNT_NOT_FOUND]: 'Kein Konto mit dieser E-Mail-Adresse gefunden.',
    [AuthErrorCode.ACCOUNT_DISABLED]: 'Dieses Benutzerkonto wurde deaktiviert. Bitte kontaktieren Sie den Support.',

    [AuthErrorCode.INVALID_TOKEN]: 'Der Zugangslink ist ungültig.',
    [AuthErrorCode.TOKEN_EXPIRED]: 'Der Zugangslink ist abgelaufen. Bitte fordern Sie einen neuen an.',
    [AuthErrorCode.TOKEN_ALREADY_USED]: 'Dieser Zugangslink wurde bereits verwendet.',
    [AuthErrorCode.TOKEN_REFRESH_FAILED]: 'Ihre Sitzung konnte nicht verlängert werden. Bitte melden Sie sich erneut an.',

    [AuthErrorCode.NETWORK_ERROR]: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
    [AuthErrorCode.SERVER_ERROR]: 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    [AuthErrorCode.SERVICE_UNAVAILABLE]: 'Der Dienst ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.',

    [AuthErrorCode.INVALID_EMAIL]: 'Die E-Mail-Adresse ist ungültig.',
    [AuthErrorCode.INVALID_PASSWORD]: 'Das Passwort ist ungültig.',

    [AuthErrorCode.NOT_SUPPORTED]: 'Diese Funktion wird nicht unterstützt.',
    [AuthErrorCode.NOT_IMPLEMENTED]: 'Diese Funktion ist noch nicht verfügbar.',

    [AuthErrorCode.UNKNOWN_ERROR]: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
};

/**
 * Get the localized error message for an auth error code
 */
export function getAuthErrorMessage(code: AuthErrorCode): string {
    return errorMessages[code] ?? errorMessages[AuthErrorCode.UNKNOWN_ERROR];
}

/**
 * Get the localized error message from an error object
 */
export function getErrorMessage(error: unknown): string {
    if (AuthError.isAuthError(error)) {
        return getAuthErrorMessage(error.code);
    }

    if (error instanceof Error) {
        return error.message;
    }

    return errorMessages[AuthErrorCode.UNKNOWN_ERROR];
}

/**
 * Map backend error responses to AuthErrorCode
 */
function mapHttpStatusToErrorCode(status: number, message?: string): AuthErrorCode {
    // Check specific error messages from backend
    const lowerMessage = message?.toLowerCase() ?? '';

    if (lowerMessage.includes('invalid email or password') || lowerMessage.includes('invalid credentials')) {
        return AuthErrorCode.INVALID_CREDENTIALS;
    }
    if (lowerMessage.includes('account not found')) {
        return AuthErrorCode.ACCOUNT_NOT_FOUND;
    }
    if (lowerMessage.includes('token expired') || lowerMessage.includes('has expired')) {
        return AuthErrorCode.TOKEN_EXPIRED;
    }
    if (lowerMessage.includes('already used')) {
        return AuthErrorCode.TOKEN_ALREADY_USED;
    }
    if (lowerMessage.includes('invalid') && (lowerMessage.includes('token') || lowerMessage.includes('refresh'))) {
        return AuthErrorCode.INVALID_TOKEN;
    }
    if (lowerMessage.includes('invalidated')) {
        return AuthErrorCode.TOKEN_EXPIRED;
    }

    // Map by HTTP status code
    switch (status) {
        case 400:
            return AuthErrorCode.INVALID_TOKEN;
        case 401:
            return AuthErrorCode.INVALID_CREDENTIALS;
        case 403:
            return AuthErrorCode.ACCOUNT_DISABLED;
        case 404:
            return AuthErrorCode.ACCOUNT_NOT_FOUND;
        case 500:
            return AuthErrorCode.SERVER_ERROR;
        case 502:
        case 503:
        case 504:
            return AuthErrorCode.SERVICE_UNAVAILABLE;
        default:
            return AuthErrorCode.UNKNOWN_ERROR;
    }
}

/**
 * Parse an error (typically from axios) into an AuthError
 */
export function parseAuthError(error: unknown): AuthError {
    // Already an AuthError
    if (AuthError.isAuthError(error)) {
        return error;
    }

    // Axios error
    if (isAxiosError(error)) {
        const status = error.response?.status;
        const responseData = error.response?.data;
        const message = typeof responseData === 'string'
            ? responseData
            : (responseData as { message?: string })?.message;

        // Network error (no response)
        if (!error.response) {
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                return new AuthError(
                    AuthErrorCode.NETWORK_ERROR,
                    getAuthErrorMessage(AuthErrorCode.NETWORK_ERROR),
                    undefined,
                    error
                );
            }
            return new AuthError(
                AuthErrorCode.UNKNOWN_ERROR,
                getAuthErrorMessage(AuthErrorCode.UNKNOWN_ERROR),
                undefined,
                error
            );
        }

        const errorCode = mapHttpStatusToErrorCode(status ?? 500, message);
        return new AuthError(
            errorCode,
            getAuthErrorMessage(errorCode),
            status,
            error
        );
    }

    // Generic Error
    if (error instanceof Error) {
        // Check for specific error messages we throw
        if (error.message.includes('Token refresh failed')) {
            return new AuthError(
                AuthErrorCode.TOKEN_REFRESH_FAILED,
                getAuthErrorMessage(AuthErrorCode.TOKEN_REFRESH_FAILED),
                undefined,
                error
            );
        }
        if (error.message.includes('not supported')) {
            return new AuthError(
                AuthErrorCode.NOT_SUPPORTED,
                getAuthErrorMessage(AuthErrorCode.NOT_SUPPORTED),
                undefined,
                error
            );
        }
        if (error.message.includes('not implemented') || error.message.includes('not available')) {
            return new AuthError(
                AuthErrorCode.NOT_IMPLEMENTED,
                getAuthErrorMessage(AuthErrorCode.NOT_IMPLEMENTED),
                undefined,
                error
            );
        }
        if (error.message.includes('Invalid token format')) {
            return new AuthError(
                AuthErrorCode.INVALID_TOKEN,
                getAuthErrorMessage(AuthErrorCode.INVALID_TOKEN),
                undefined,
                error
            );
        }
    }

    // Unknown error type
    return new AuthError(
        AuthErrorCode.UNKNOWN_ERROR,
        getAuthErrorMessage(AuthErrorCode.UNKNOWN_ERROR),
        undefined,
        error
    );
}

/**
 * Type guard for AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
    return (
        typeof error === 'object' &&
        error !== null &&
        'isAxiosError' in error &&
        (error as AxiosError).isAxiosError === true
    );
}
